import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/api';
import { useTheme } from '../App';

const IdeasVault = () => {
  const { t, i18n } = useTranslation();
  const { darkMode } = useTheme();
  const isRTL = i18n.language === 'ar';
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    industry: 'all',
    executionDifficulty: 'all',
    competition: 'all',
    regulatoryRisk: 'all',
    timing: 'all'
  });

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ideasPerPage = 15;

  // Fetch ideas from API with server-side pagination
  const fetchIdeas = async (page = currentPage, appliedFilters = filters) => {
    try {
      setLoading(true);
      const response = await apiClient.getIdeas(appliedFilters, page, ideasPerPage, i18n.language, searchQuery);
      const ideas = response.ideas || response.data || response;
      
      // Transform Supabase response format for each idea
      const transformedIdeas = ideas.map(idea => {
        if (idea) {
          // Transform competitors from array or null to array
          idea.competitors = idea.idea_competitors || idea.competitors || [];
          
          // Transform financial data from array to object
          idea.financialData = idea.idea_financial_data?.[0] || idea.financialData || null;
          
          // Transform technical requirements from array to object  
          idea.technicalRequirements = idea.idea_technical_requirements?.[0] || idea.technicalRequirements || null;
        }
        return idea;
      });
      
      setIdeas(transformedIdeas);
      setTotalPages(response.totalPages || Math.ceil((response.totalCount || 0) / ideasPerPage));
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch ideas:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount and language change
  useEffect(() => {
    fetchIdeas(1, filters);
    setCurrentPage(1);
  }, [i18n.language, searchQuery]); // Re-fetch when language changes or search query changes

  const filterOptions = {
    type: ['SaaS', 'Mobile app', 'E-commerce website', 'Platform'],
    industry: ['Education', 'Entertainment', 'Sports', 'Shopping', 'Social Media', 'Self-Development', 'Productivity', 'E-commerce', 'Services', 'Fashion'],
    executionDifficulty: ['Easy', 'Moderate', 'Complex'],
    competition: ['No competition', 'Low competition', 'High Competition'],
    regulatoryRisk: ['No risk', 'Low risk', 'Medium risk', 'High risk'],
    timing: ['Perfect timing', 'Late timing', 'Moderate Timing']
  };

  // Helper function to get translated filter option
  const getFilterOptionLabel = (category, option) => {
    return t(`filterOptions.${category}.${option}`, option);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    
    // Map camelCase filter keys to snake_case field names for API
    const apiFilters = {};
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] !== 'all') {
        const fieldName = key === 'executionDifficulty' ? 'execution_difficulty' :
                         key === 'regulatoryRisk' ? 'regulatory_risk' :
                         key;
        apiFilters[fieldName] = newFilters[key];
      }
    });

    setCurrentPage(1); // Reset to first page when filters change
    fetchIdeas(1, apiFilters); // Fetch with new filters from server
  };

  const clearFilters = () => {
    const clearedFilters = {
      type: 'all',
      industry: 'all',
      executionDifficulty: 'all',
      competition: 'all',
      regulatoryRisk: 'all',
      timing: 'all'
    };
    setFilters(clearedFilters);
    setSearchQuery('');
    setCurrentPage(1);
    fetchIdeas(1, {}); // Fetch all ideas without filters
  };

  const handleSearch = (search) => {
    setSearchQuery(search);
    setCurrentPage(1);

    // Map current filters to API format
    const apiFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] !== 'all') {
        const fieldName = key === 'executionDifficulty' ? 'execution_difficulty' :
                         key === 'regulatoryRisk' ? 'regulatory_risk' :
                         key;
        apiFilters[fieldName] = filters[key];
      }
    });

    fetchIdeas(1, apiFilters);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-green-600';
      case 'Moderate':
        return 'text-yellow-600';
      case 'Complex':
        return 'text-red-600';
      default:
        return darkMode ? 'text-gray-300' : 'text-slate-500';
    }
  };

  // Server-side pagination - ideas are already the current page
  const getCurrentPageIdeas = () => {
    return ideas; // Ideas array already contains only the current page
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    
    // Map current filters to API format
    const apiFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] !== 'all') {
        const fieldName = key === 'executionDifficulty' ? 'execution_difficulty' :
                         key === 'regulatoryRisk' ? 'regulatory_risk' :
                         key;
        apiFilters[fieldName] = filters[key];
      }
    });
    
    fetchIdeas(pageNumber, apiFilters); // Fetch new page from server
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    // Previous button
    if (currentPage > 1) {
      buttons.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className={`px-3 py-2 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-white text-slate-700 hover:bg-gray-50 shadow-soft-sm'
          }`}
        >
          {isRTL ? '→' : '←'}
        </button>
      );
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            i === currentPage
              ? 'bg-gradient-fuchsia text-white'
              : darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-white text-slate-700 hover:bg-gray-50 shadow-soft-sm'
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      buttons.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className={`px-3 py-2 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-white text-slate-700 hover:bg-gray-50 shadow-soft-sm'
          }`}
        >
          {isRTL ? '←' : '→'}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="fade-in-soft">
      {/* Hero Section */}
      <div className="mb-12">
        <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>
          {t('navigation.ideasVault')}
        </h1>
        <p className={`text-xl mb-8 ${
          darkMode ? 'text-gray-300' : 'text-slate-500'
        } text-start`}>
          {t('ideasVault.explore')}
        </p>
        <div className={`w-24 h-1 bg-gradient-fuchsia rounded-full ${isRTL ? 'ml-0 mr-auto' : 'ml-0 mr-auto'}`}></div>
      </div>

      {/* Search Section */}
      <div className="mb-6">
        <div className={`relative ${isRTL ? 'text-right' : 'text-left'}`}>
          <input
            type="text"
            placeholder={t('ideasVault.searchPlaceholder', 'Search ideas by name...')}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className={`input-soft w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3`}
          />
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
            <svg className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Filters Toggle */}
      <div className={`mb-8 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            darkMode 
              ? 'bg-gray-800 text-white hover:opacity-90' 
              : 'bg-white text-slate-700 hover:bg-gray-50 shadow-soft-md'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {t('ideasVault.filterIdeas')}
          <svg className={`w-4 h-4 transition-transform ${
            showFilters ? 'rotate-180' : ''
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className={`text-sm ${
          darkMode ? 'text-gray-300' : 'text-slate-500'
        }`}>
          {t('ideasVault.showing', { filtered: ideas?.length || 0, total: totalCount || 0 })}
          {totalPages > 1 && (
            <span className={`block mt-1 ${
              darkMode ? 'text-gray-400' : 'text-slate-400'
            }`}>
              {t('ideasVault.page', { current: currentPage, total: totalPages })}
            </span>
          )}
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className={`mb-8 p-6 rounded-2xl shadow-soft-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-xl font-semibold mb-6 ${
            darkMode ? 'text-white' : 'text-slate-700'
          }`}>
            {t('ideasVault.filterIdeas')}
          </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.typeOfIdea')}</label>
            <select 
              className="select-soft w-full"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="all">{t('ideasVault.allTypes')}</option>
              {filterOptions.type.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('type', option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.industry')}</label>
            <select 
              className="select-soft w-full"
              value={filters.industry}
              onChange={(e) => handleFilterChange('industry', e.target.value)}
            >
              <option value="all">{t('ideasVault.allIndustries')}</option>
              {filterOptions.industry.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('industry', option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.executionDifficulty')}</label>
            <select 
              className="select-soft w-full"
              value={filters.executionDifficulty}
              onChange={(e) => handleFilterChange('executionDifficulty', e.target.value)}
            >
              <option value="all">{t('ideasVault.allDifficulties')}</option>
              {filterOptions.executionDifficulty.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('executionDifficulty', option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.competition')}</label>
            <select 
              className="select-soft w-full"
              value={filters.competition}
              onChange={(e) => handleFilterChange('competition', e.target.value)}
            >
              <option value="all">{t('ideasVault.allLevels')}</option>
              {filterOptions.competition.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('competition', option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.regulatoryRisk')}</label>
            <select 
              className="select-soft w-full"
              value={filters.regulatoryRisk}
              onChange={(e) => handleFilterChange('regulatoryRisk', e.target.value)}
            >
              <option value="all">{t('ideasVault.allRiskLevels')}</option>
              {filterOptions.regulatoryRisk.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('regulatoryRisk', option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-slate-600'
            } text-start`}>{t('ideasVault.timing')}</label>
            <select 
              className="select-soft w-full"
              value={filters.timing}
              onChange={(e) => handleFilterChange('timing', e.target.value)}
            >
              <option value="all">{t('ideasVault.allTimings')}</option>
              {filterOptions.timing.map(option => (
                <option key={option} value={option}>{getFilterOptionLabel('timing', option)}</option>
              ))}
            </select>
          </div>
        </div>
        
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
            <button 
              onClick={clearFilters} 
              className="btn-soft btn-soft-secondary"
            >
              {t('ideasVault.clearFilters')}
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={`p-6 rounded-2xl shadow-soft-lg mb-8 ${
          darkMode ? 'bg-red-900 bg-opacity-20 border border-red-500' : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-center ${
            darkMode ? 'text-red-300' : 'text-red-600'
          }`}>
            Error loading ideas: {error}
          </p>
        </div>
      )}

      {/* Ideas Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ideas && ideas.length > 0 ? getCurrentPageIdeas().map(idea => (
          <Link key={idea.id} to={`/idea/${idea.id}`} className="block hover:no-underline">
            <div className={`p-6 hover-soft rounded-2xl shadow-soft-lg h-full flex flex-col ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-start justify-between mb-4">
                <div className="badge-soft badge-soft-primary">{getFilterOptionLabel('type', idea.type)}</div>
                <div className={`flex flex-col space-y-1 text-sm ${
                  isRTL ? 'items-start' : 'items-end'
                } ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  <span>{getFilterOptionLabel('industry', idea.industry)}</span>
                  <span className={`font-medium ${getDifficultyColor(idea.execution_difficulty)}`}>{getFilterOptionLabel('executionDifficulty', idea.execution_difficulty)}</span>
                </div>
              </div>
              
              <h3 className={`text-xl font-bold mb-3 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>{idea.title}</h3>
              <p className={`leading-relaxed mb-4 flex-grow ${
                darkMode ? 'text-gray-300' : 'text-slate-600'
              }`}>
                {idea.description.length > 120 
                  ? `${idea.description.substring(0, 120)}...` 
                  : idea.description
                }
              </p>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className={`rounded-lg p-2 text-center ${
                  darkMode ? 'bg-white bg-opacity-20' : 'bg-gray-100'
                }`}>
                  <div className={`text-xs font-medium ${
                    darkMode ? 'text-white' : 'text-slate-600'
                  }`}>{t('ideasVault.competition')}</div>
                  <div className={`text-xs ${
                    darkMode ? 'text-gray-300' : 'text-slate-500'
                  }`}>{getFilterOptionLabel('competition', idea.competition)}</div>
                </div>
                <div className={`rounded-lg p-2 text-center ${
                  darkMode ? 'bg-white bg-opacity-20' : 'bg-gray-100'
                }`}>
                  <div className={`text-xs font-medium ${
                    darkMode ? 'text-white' : 'text-slate-600'
                  }`}>{t('ideasVault.risk')}</div>
                  <div className={`text-xs ${
                    darkMode ? 'text-gray-300' : 'text-slate-500'
                  }`}>{getFilterOptionLabel('regulatoryRisk', idea.regulatory_risk)}</div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-slate-500'
                }`}>
                  {t('ideasVault.clickToView')}
                </div>
                <div className={`flex items-center font-medium ${
                  darkMode ? 'text-white' : 'text-indigo-600'
                }`}>
                  <span className={isRTL ? 'ml-1' : 'mr-1'}>{t('ideasVault.viewDetails')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </Link>
        )) : (
          <div className="col-span-full text-center py-16">
            {loading ? (
              <div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>Loading ideas...</p>
              </div>
            ) : (
              <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>No ideas available</p>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {ideas && ideas.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center mt-12 space-x-2">
          {renderPaginationButtons()}
        </div>
      )}

      {/* Empty State */}
      {!loading && ideas && ideas.length === 0 && (
        <div className={`text-center py-16 rounded-2xl shadow-soft-lg col-span-full ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-2xl font-bold mb-4 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>{t('ideasVault.noIdeasFound')}</h3>
          <p className={`mb-6 ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>{t('ideasVault.tryAdjustingFilters')}</p>
          <button 
            onClick={clearFilters} 
            className="btn-soft btn-soft-primary"
          >
            {t('ideasVault.clearAllFilters')}
          </button>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="mt-12">
        <div className={`p-8 rounded-2xl shadow-soft-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-2xl font-bold mb-4 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>{t('ideasVault.readyToBuild')}</h3>
          <p className={`mb-6 ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>
            {t('ideasVault.takeToImagineLab')}
          </p>
          <Link to="/imagine-lab" className="btn-soft btn-soft-primary">
            {t('ideasVault.goToImagineLab')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IdeasVault;